import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartColor, getChartGridColor } from '@t-one-internal/design-system';

// Support both snake_case (API) and camelCase formats
interface EngagementTrendData {
  period?: string;
  sort_date?: string;
  sortDate?: string;
  enps_score?: number | null;
  enpsScore?: number | null;
  satisfaction_score?: number | null;
  satisfactionScore?: number | null;
}

interface EngagementScoreTrendsWidgetProps {
  data?: EngagementTrendData | EngagementTrendData[] | { data: EngagementTrendData[] };
  loading?: boolean;
  error?: string;
}

// Helper to extract array from various data formats
const extractDataArray = (
  data: EngagementScoreTrendsWidgetProps['data']
): EngagementTrendData[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if ((data as { data: EngagementTrendData[] })?.data) {
    return (data as { data: EngagementTrendData[] }).data;
  }
  return [data as EngagementTrendData];
};

// Helpers for snake_case/camelCase compatibility
const getEnpsScore = (d: EngagementTrendData): number | null => {
  const val = d.enpsScore ?? d.enps_score;
  return val !== undefined && val !== null ? Number(val) : null;
};

const getSatisfactionScore = (d: EngagementTrendData): number | null => {
  const val = d.satisfactionScore ?? d.satisfaction_score;
  return val !== undefined && val !== null ? Number(val) : null;
};

const getPeriod = (d: EngagementTrendData): string => d.period ?? '';

interface ChartDataPoint {
  period: string;
  enps: number | null;
  satisfaction: number | null;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    color: string;
    name: string;
    value: number | null;
    dataKey: string;
  }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '10px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        border: '1px solid #E5E7EB',
      }}
    >
      <div
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
          marginBottom: '6px',
        }}
      >
        {label}
      </div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '2px 0',
            fontSize: '12px',
          }}
        >
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: '#6B7280', flex: 1 }}>{entry.name}</span>
          <span
            style={{
              color: '#111827',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {entry.value !== null
              ? entry.dataKey === 'enps'
                ? `${entry.value > 0 ? '+' : ''}${Math.round(entry.value)}`
                : entry.value.toFixed(1)
              : 'N/A'}
          </span>
        </div>
      ))}
    </div>
  );
};

export const EngagementScoreTrendsWidget: React.FC<EngagementScoreTrendsWidgetProps> = ({
  data,
  loading,
  error,
}) => {
  const chartData = useMemo((): ChartDataPoint[] => {
    const rawData = extractDataArray(data);
    if (!rawData.length) return [];

    return rawData.map((d) => ({
      period: getPeriod(d),
      enps: getEnpsScore(d),
      satisfaction: getSatisfactionScore(d),
    }));
  }, [data]);

  // Check which series have data to adjust layout
  const hasEnps = useMemo(() => chartData.some((d) => d.enps !== null), [chartData]);
  const hasEngagement = useMemo(() => chartData.some((d) => d.satisfaction !== null), [chartData]);

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">
            Engagement Score Trends
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">
            Engagement Score Trends
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="p-5 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">
            Engagement Score Trends
          </h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 gap-2">
          <span className="text-gray-400 text-sm">No engagement survey data yet</span>
          <span className="text-gray-400 text-xs">
            Create and run an eNPS or Engagement survey to see score trends here.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-semibold text-gray-900 text-sm">
          Engagement Score Trends
        </h3>
      </div>

      {/* Chart */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
          >
            <CartesianGrid
              stroke={getChartGridColor('grid')}
              strokeDasharray="none"
              vertical={false}
            />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 11, fill: getChartGridColor('axisLabel') }}
              axisLine={{ stroke: getChartGridColor('axis') }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            {/* Left Y-axis: eNPS (-100 to +100) */}
            <YAxis
              yAxisId="enps"
              domain={[-100, 100]}
              tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v}`}
              tick={{ fontSize: 11, fill: ChartColor.blue }}
              axisLine={{ stroke: getChartGridColor('axis') }}
              tickLine={false}
              width={40}
              hide={!hasEnps}
            />
            {/* Right Y-axis: Engagement (0-10) */}
            <YAxis
              yAxisId="engagement"
              orientation="right"
              domain={[0, 10]}
              tickFormatter={(v: number) => v.toFixed(1)}
              tick={{ fontSize: 11, fill: ChartColor.emerald }}
              axisLine={{ stroke: getChartGridColor('axis') }}
              tickLine={false}
              width={36}
              hide={!hasEngagement}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            <Legend
              iconType="plainline"
              iconSize={20}
              wrapperStyle={{
                fontSize: '12px',
                color: '#374151',
                paddingTop: '8px',
              }}
            />
            {/* eNPS - Solid blue line (left Y-axis, -100 to +100) */}
            <Line
              yAxisId="enps"
              type="monotone"
              dataKey="enps"
              name="eNPS"
              stroke={ChartColor.blue}
              strokeWidth={2}
              dot={{ r: 4, fill: ChartColor.blue, stroke: ChartColor.blue, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: ChartColor.blue, stroke: '#fff', strokeWidth: 2 }}
              connectNulls
            />
            {/* Engagement - Dashed green line (right Y-axis, 0-10) */}
            <Line
              yAxisId="engagement"
              type="monotone"
              dataKey="satisfaction"
              name="Engagement"
              stroke={ChartColor.emerald}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4, fill: ChartColor.emerald, stroke: ChartColor.emerald, strokeWidth: 0 }}
              activeDot={{ r: 5, fill: ChartColor.emerald, stroke: '#fff', strokeWidth: 2 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EngagementScoreTrendsWidget;
